import { Link } from '@inertiajs/react';
import { format } from 'date-fns';
import { Calendar, Eye, FileText, Plus, Share2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import AppLayout from '@/layouts/app-layout';


interface Share {
  id: number;
  title: string;
  description: string;
  slug: string;
  expires_at: string | null;
  view_count: number;
  created_at: string;
  public_url: string;
  files_count: number;
}

interface SharesIndexProps {
  shares: Share[];
}

function formatDate(dateString: string) {
  try {
    return format(new Date(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
}

export default function SharesIndex({ shares }: SharesIndexProps) {
  const handleCopyLink = (url: string) => {
    void navigator.clipboard.writeText(url);
    // You could add a toast notification here
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this share?')) {
      // Implement delete functionality
      console.log('Deleting share:', id);
    }
  };

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">My Shares</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage your shared files and documents
            </p>
          </div>
          <Link href="/shares/new">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Share
            </Button>
          </Link>
        </div>

        {shares.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Share2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No shares yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Create your first share to get started
              </p>
              <Link href="/shares/new">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Share
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {shares.map((share) => (
              <Card key={share.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="line-clamp-1">
                        <Link 
                          href={`/shares/${share.id}`}
                          className="hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          {share.title}
                        </Link>
                      </CardTitle>
                      <CardDescription className="line-clamp-2 mt-2">
                        {share.description || 'No description'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center text-gray-600 dark:text-gray-400">
                        <FileText className="w-4 h-4 mr-1" />
                        {share.files_count} {share.files_count === 1 ? 'file' : 'files'}
                      </span>
                      <span className="flex items-center text-gray-600 dark:text-gray-400">
                        <Eye className="w-4 h-4 mr-1" />
                        {share.view_count} views
                      </span>
                    </div>
                    
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Calendar className="w-4 h-4 mr-1" />
                      Created {formatDate(share.created_at)}
                    </div>

                    {share.expires_at && (
                      <div className="text-orange-600 dark:text-orange-400">
                        Expires {formatDate(share.expires_at)}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyLink(share.public_url)}
                      className="flex-1"
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                    <Link href={`/shares/${share.id}/edit`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        Edit
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(share.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}